/**
 * ความพร้อมปิดรอบ (Close Readiness, เรื่อง 10) — checklist before locking a
 * payroll period. 🔴 items block a clean close (lock asks for an acknowledge
 * reason); 🟡 items are informational. Each links to where it gets cleared.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, AlertTriangle, Clock, Wallet, ChevronDown, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import { Loading } from '../../components/layout/Loading';
import { useCloseReadiness, useEmployees, type ReadinessItem } from '../../hooks';

/** ปลายทางระดับหมวด (fallback + สำหรับรายการที่นับรวมไม่มีรายชื่อรายคน). */
const ITEM_ROUTE: Record<string, string> = {
  forgot_payroll: '/owner/cycle#payroll',
  missing_salary_base: '/hr/people#all',
  unresolved: '/approvals#emergency',
  escalation_pending: '/approvals',
  leave_pending: '/approvals#leave-ot',
  ot_pending: '/approvals#leave-ot',
  missing_summary: '/owner/cycle#reconcile',
  terminated_no_finalpay: '/hr/people#termination',
  net_variance: '/owner/cycle#payroll',
  unpaired_line: '/hr/people#all',
};

type EmpLite = { emp_code: string; first_name?: string | null; last_name?: string | null; nickname?: string | null };

/** ดึงรหัสพนักงานรายคนออกจาก detail ของแต่ละรายการ (backend cap ที่ 50 คน). */
function itemEmpCodes(it: ReadinessItem): string[] {
  const d = it.detail as { emp_codes?: unknown; missing?: unknown; swung?: unknown };
  if (Array.isArray(d.emp_codes)) return d.emp_codes as string[];
  if (it.key === 'net_variance') {
    const missing = Array.isArray(d.missing) ? (d.missing as string[]) : [];
    const swung = Array.isArray(d.swung) ? (d.swung as { emp_code: string }[]).map((s) => s.emp_code) : [];
    return [...missing, ...swung];
  }
  return [];
}

/** หน้าบุคคลปลายทางของแต่ละคน — กดแล้วเด้งไปเปิดโปรไฟล์คนนั้นทันที. */
function personRoute(key: string, empCode: string): string {
  const enc = encodeURIComponent(empCode);
  if (key === 'terminated_no_finalpay') return `/hr/people?emp=${enc}#termination`;
  return `/hr/people?emp=${enc}#all`;
}

function Row({ it, nameOf }: { it: ReadinessItem; nameOf: (code: string) => string }) {
  const [open, setOpen] = useState(false);
  const Icon = it.severity === 'red' ? AlertCircle : AlertTriangle;
  const textColor = it.severity === 'red' ? 'text-red-600' : 'text-amber-600';
  const borderColor = it.severity === 'red' ? 'border-l-red-500' : 'border-l-amber-400';
  const codes = itemEmpCodes(it);
  const categoryTo = ITEM_ROUTE[it.key];

  // มีรายชื่อรายคน → กางดูได้ + กดชื่อเด้งไปหน้าบุคคลนั้น
  if (codes.length > 0) {
    const extra = it.count - codes.length; // เกิน 50 คน backend ตัด
    return (
      <div className={`border-l-4 ${borderColor} bg-white rounded-r-lg overflow-hidden`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50"
        >
          <Icon className={`w-5 h-5 shrink-0 ${textColor}`} />
          <span className="text-sm flex-1">{it.label}</span>
          <span className="text-sm font-semibold tabular-nums">{it.count}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="border-t border-gray-100">
            {codes.map((code) => (
              <Link
                key={code}
                to={personRoute(it.key, code)}
                className="flex items-center gap-2 pl-11 pr-3 py-2 text-sm hover:bg-brand-50/60 border-t border-gray-50 first:border-t-0"
              >
                <span className="flex-1 truncate">{nameOf(code)}</span>
                <span className="text-xs text-gray-400 tabular-nums shrink-0">{code}</span>
                <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
              </Link>
            ))}
            {extra > 0 && categoryTo && (
              <Link to={categoryTo} className="block pl-11 pr-3 py-2 text-xs text-brand-600 hover:bg-brand-50/60 border-t border-gray-50">
                ดูทั้งหมดอีก {extra} คน →
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }

  // นับรวม ไม่มีรายชื่อรายคน (เรื่องค้าง/ลา/OT รออนุมัติ) → ลิงก์ทั้งแถวไปหน้ารวมเดิม
  const body = (
    <div className={`flex items-center gap-3 border-l-4 ${borderColor} bg-white rounded-r-lg px-3 py-3`}>
      <Icon className={`w-5 h-5 shrink-0 ${textColor}`} />
      <span className="text-sm flex-1">{it.label}</span>
      <span className="text-sm font-semibold tabular-nums">{it.count}</span>
    </div>
  );
  return categoryTo ? <Link to={categoryTo} className="block hover:opacity-80">{body}</Link> : body;
}

/** Content-only version used by the PayrollCycle hub. */
export function CloseReadinessContent() {
  const [period] = useState(() => new Date().toISOString().slice(0, 7));
  const { data, isLoading } = useCloseReadiness(period);
  // ดึงชื่อพนักงาน (รวมคนออกแล้ว) ไว้แปลง emp_code → "ชื่อ สกุล (ชื่อเล่น)" ในรายชื่อที่กางดู
  const { data: empRows } = useEmployees(true, true) as { data: EmpLite[] | undefined };
  const nameOf = (code: string) => {
    const e = empRows?.find((r) => r.emp_code === code);
    if (!e) return code;
    const full = `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim();
    const nn = e.nickname ? ` (${e.nickname})` : '';
    return full ? `${full}${nn}` : code;
  };
  if (isLoading || !data) return <Loading />;

  const hard = data.items.filter((i) => i.hardBlock && i.count > 0);
  const red = data.items.filter((i) => i.affectsCycle && !i.hardBlock && i.count > 0);
  const yellow = data.items.filter((i) => !i.affectsCycle && i.count > 0);
  // ก่อนถึงรอบปิดงวด (และยังไม่มีอะไรบล็อกจริง) → โทนสงบ ไม่เร่งเตือน.
  const earlyCalm = !data.dueNow && data.hardBlockCount === 0 && data.affectsCycleCount === 0;

  return (
    <>
      <PayrollPipeline current="close" period={period} />
      <div className="space-y-5 max-w-2xl">
        {data.mode === 'salary_only' && (
          <div className="card p-3 flex items-center gap-2.5 border-l-4 border-l-brand-400 bg-brand-50/60">
            <Wallet className="w-5 h-5 text-brand-600 shrink-0" />
            <p className="text-sm text-gray-700">
              โหมดเงินเดือนล้วน — คิดจากฐานเงินเดือน เงินเพิ่ม และรายการหัก ไม่นำการลงเวลา/ลา/OT มาคิด
            </p>
          </div>
        )}

        {data.ready ? (
          earlyCalm ? (
            <div className="card p-4 flex items-start gap-3 border-l-4 border-l-gray-300">
              <Clock className="w-6 h-6 text-gray-400 shrink-0" />
              <div>
                <p className="font-medium">ยังไม่ถึงรอบปิดงวด</p>
                <p className="text-sm text-gray-500">
                  ค่อยมาปิดงวดช่วงสิ้นเดือน (ราววันที่ {data.cutoffDay}) ตอนนี้ยังไม่มีอะไรต้องรีบ
                </p>
              </div>
            </div>
          ) : (
            <div className="card p-4 flex items-center gap-3 border-l-4 border-l-brand-500">
              <CheckCircle2 className="w-6 h-6 text-brand-600" />
              <div>
                <p className="font-medium">พร้อมปิดรอบ</p>
                <p className="text-sm text-gray-500">ไม่มีเรื่องค้างที่กระทบรอบเงินเดือน</p>
              </div>
            </div>
          )
        ) : data.hardBlockCount > 0 ? (
          <div className="card p-4 border-l-4 border-l-red-600 bg-red-50">
            <p className="font-medium text-red-700">ปิดงวดไม่ได้ — มี {data.hardBlockCount} รายการต้องแก้ก่อน</p>
            <p className="text-sm text-gray-600">ลืมทำเงินเดือน / ยังไม่ตั้งฐานเงินเดือน — แก้ให้ครบก่อน ปิดข้ามไม่ได้</p>
          </div>
        ) : (
          <div className="card p-4 border-l-4 border-l-red-500">
            <p className="font-medium text-red-700">มี {data.affectsCycleCount} เรื่องต้องเคลียร์ก่อนปิด</p>
            <p className="text-sm text-gray-500">ปิดได้แต่ต้องยืนยันพร้อมเหตุผล (บันทึกประวัติ)</p>
          </div>
        )}

        {hard.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-red-700">ต้องแก้ก่อน (ปิดข้ามไม่ได้)</h3>
            {hard.map((it) => <Row key={it.key} it={it} nameOf={nameOf} />)}
          </div>
        )}

        {red.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">ต้องเคลียร์ (กระทบรอบ)</h3>
            {red.map((it) => <Row key={it.key} it={it} nameOf={nameOf} />)}
          </div>
        )}

        {yellow.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              {earlyCalm ? 'ดูไว้ก่อนได้ (ค่อยจัดการตอนสิ้นงวด)' : 'ควรตรวจ (ไม่บล็อก)'}
            </h3>
            {yellow.map((it) => <Row key={it.key} it={it} nameOf={nameOf} />)}
          </div>
        )}
      </div>
    </>
  );
}

/** Standalone page kept for legacy /owner/close-readiness (now redirects). */
export function CloseReadiness() {
  return (
    <AdminLayout title="ความพร้อมปิดรอบ">
      <CloseReadinessContent />
    </AdminLayout>
  );
}
